import { useState, useRef } from 'react';
import {
  Card,
  CardHeader,
  Button,
  Badge,
  LoadingSkeleton,
  Modal,
  Input,
  useToast,
} from '@nexora/design-system';
import {
  useReconciliations,
  useStartReconciliation,
  useConfirmReconciliation,
  type Reconciliation,
  type StatementTransaction,
} from './useReconciliation';
import { useBankAccounts } from './useBankAccounts';
import { ReconciliationWorkspace } from './ReconciliationWorkspace';
import { formatCurrency, formatDate } from '../../shared/utils/format';

/* ── Helpers ── */

const STATUS_VARIANTS: Record<string, 'info' | 'success' | 'warning'> = {
  DRAFT: 'info',
  CONFIRMED: 'success',
};

const STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Rascunho',
  CONFIRMED: 'Confirmada',
};

/* ── Page ── */

export function ReconciliationPage(): JSX.Element {
  const { addToast } = useToast();
  const { data: accounts } = useBankAccounts();
  const { data: reconciliationsData, isLoading, error, refetch } = useReconciliations();
  const startRecon = useStartReconciliation();
  const confirmRecon = useConfirmReconciliation();

  const reconciliations = reconciliationsData?.data ?? [];
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showStartModal, setShowStartModal] = useState(false);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  // Start reconciliation form
  const [accountId, setAccountId] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [openingBalance, setOpeningBalance] = useState('');
  const [closingBalance, setClosingBalance] = useState('');
  const [statementDate, setStatementDate] = useState('');
  const [importedTx, setImportedTx] = useState<StatementTransaction[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);


  const resetForm = (): void => {
    setAccountId('');
    setPeriodStart('');
    setPeriodEnd('');
    setOpeningBalance('');
    setClosingBalance('');
    setStatementDate('');
    setImportedTx([]);
  };

  const handleOfxUpload = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Parse OFX content — simplified for CSV preview; real OFX would use a parser
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target?.result as string;
      const lines = text.split('\n').filter((l) => l.trim());
      if (lines.length < 2) {
        addToast('Arquivo deve conter cabeçalho + pelo menos 1 transação', 'error');
        return;
      }

      // Parse as CSV: date,value,description
      const transactions: StatementTransaction[] = lines.slice(1).map((line, idx) => {
        const parts = line.split(',').map((p) => p.trim().replace(/^"/, '').replace(/"$/, ''));
        return {
          transactionId: parts[0] ?? `tx-${idx + 1}`,
          date: parts[1] ?? new Date().toISOString(),
          value: Number(parts[2] ?? 0),
          description: parts[3] ?? parts[0] ?? `Transação ${idx + 1}`,
        };
      }).filter((t) => t.value !== 0);

      setImportedTx(transactions);
      addToast(`${transactions.length} transação(ões) carregada(s)`, 'success');
    };
    reader.readAsText(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleStart = async (): Promise<void> => {
    if (!accountId || !periodStart || !periodEnd || !openingBalance || !closingBalance || !statementDate) {
      addToast('Preencha todos os campos obrigatórios', 'error');
      return;
    }
    if (importedTx.length === 0) {
      addToast('Importe ao menos uma transação do extrato', 'error');
      return;
    }

    try {
      await startRecon.mutateAsync({
        accountId,
        openingBalance: Number(openingBalance),
        closingBalance: Number(closingBalance),
        statementDate: new Date(statementDate).toISOString(),
        periodStart: new Date(periodStart).toISOString(),
        periodEnd: new Date(periodEnd).toISOString(),
        importedTransactions: importedTx,
      });
      addToast('Conciliação iniciada com sucesso', 'success');
      setShowStartModal(false);
      resetForm();
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Erro ao iniciar conciliação', 'error');
    }
  };

  const handleConfirm = async (id: string): Promise<void> => {
    try {
      await confirmRecon.mutateAsync(id);
      addToast('Conciliação confirmada com sucesso', 'success');
      setConfirmId(null);
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Erro ao confirmar conciliação', 'error');
    }
  };

  return (
    <div className="recon-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Conciliação Bancária</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Conciliação de extrato bancário com os títulos.</p>
        </div>
        {!selectedId && (
          <div className="flex flex-wrap gap-sm">
            <Button onClick={() => { resetForm(); setShowStartModal(true); }}>+ Nova Conciliação</Button>
          </div>
        )}
      </div>

      {selectedId && (
        <ReconciliationWorkspace
          reconciliationId={selectedId}
          onBack={() => setSelectedId(null)}
          onConfirm={(id) => setConfirmId(id)}
          confirming={confirmRecon.isPending && confirmId === selectedId}
        />
      )}

      {/* ── Loading ── */}
      {!selectedId && isLoading && (
        <Card padding="1rem">
          <LoadingSkeleton count={4} height="48px" />
        </Card>
      )}

      {/* ── Error ── */}
      {!selectedId && error && !isLoading && (
        <Card padding="2rem" className="state-card">
          <div className="state-error" role="alert">
            <p>Erro ao carregar conciliações</p>
            <p className="state-detail">{(error as Error).message}</p>
            <Button size="sm" variant="outline" onClick={() => refetch()}>Tentar novamente</Button>
          </div>
        </Card>
      )}

      {/* ── Empty ── */}
      {!selectedId && !isLoading && !error && reconciliations.length === 0 && (
        <Card padding="2rem" className="state-card">
          <div className="state-empty">
            <p>Nenhuma conciliação encontrada</p>
            <p className="state-detail">
              Importe um extrato bancário (OFX ou CSV) e inicie uma conciliação para começar.
            </p>
          </div>
        </Card>
      )}

      {/* ── List ── */}
      {!selectedId && !isLoading && !error && reconciliations.length > 0 && (
        <Card padding="0">
          <CardHeader
            className="px-md pt-md"
            action={
              <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
                {reconciliations.length ?? 0} {(reconciliations.length ?? 0) === 1 ? 'conciliação' : 'conciliações'}
              </span>
            }
          >
            <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Conciliações</h3>
          </CardHeader>
          <div className="recon-table-wrap">
            <table className="recon-table">
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Período</th>
                  <th>Saldo Inicial</th>
                  <th>Saldo Final</th>
                  <th>Diferença</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {reconciliations.map((r: Reconciliation) => (
                  <tr key={r.id}>
                    <td>{r.accountName || r.accountId.slice(0, 8)}</td>
                    <td>{formatDate(r.periodStart)} - {formatDate(r.periodEnd)}</td>
                    <td>{formatCurrency(r.openingBalance)}</td>
                    <td>{formatCurrency(r.closingBalance)}</td>
                    <td className={r.difference !== 0 ? 'recon-diff-warn' : ''}>
                      {formatCurrency(r.difference)}
                    </td>
                    <td>
                      <Badge variant={STATUS_VARIANTS[r.status] ?? 'warning'} size="sm">
                        {STATUS_LABELS[r.status] ?? r.status}
                      </Badge>
                    </td>
                    <td>
                      <div className="recon-actions">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setSelectedId(r.id)}
                        >
                          Abrir
                        </Button>
                        {r.status === 'DRAFT' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            style={{ color: '#002045' }}
                            onClick={() => setConfirmId(r.id)}
                            loading={confirmRecon.isPending && confirmId === r.id}
                          >
                            Confirmar
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* ── Start Reconciliation Modal ── */}
      <Modal
        isOpen={showStartModal}
        onClose={() => setShowStartModal(false)}
        title="Nova Conciliação"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowStartModal(false)}>Cancelar</Button>
            <Button onClick={handleStart} loading={startRecon.isPending}>Iniciar Conciliação</Button>
          </>
        }
      >
        <div className="recon-form">
          <select
            className="recon-form__select"
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
          >
            <option value="">Selecione a conta bancária</option>
            {accounts?.map((acc) => (
              <option key={acc.id} value={acc.id}>{acc.name}</option>
            ))}
          </select>

          <div className="recon-form__row">
            <Input
              label="Data do Extrato *"
              type="date"
              value={statementDate}
              onChange={(e) => setStatementDate(e.target.value)}
            />
            <Input
              label="Início do Período *"
              type="date"
              value={periodStart}
              onChange={(e) => setPeriodStart(e.target.value)}
            />
            <Input
              label="Fim do Período *"
              type="date"
              value={periodEnd}
              onChange={(e) => setPeriodEnd(e.target.value)}
            />
          </div>

          <div className="recon-form__row">
            <Input
              label="Saldo Inicial (extrato) *"
              type="number"
              step="0.01"
              value={openingBalance}
              onChange={(e) => setOpeningBalance(e.target.value)}
            />
            <Input
              label="Saldo Final (extrato) *"
              type="number"
              step="0.01"
              value={closingBalance}
              onChange={(e) => setClosingBalance(e.target.value)}
            />
          </div>

          <div className="recon-form__upload">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.ofx"
              style={{ display: 'none' }}
              onChange={handleOfxUpload}
            />
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={startRecon.isPending}
            >
              Importar Extrato (CSV/OFX)
            </Button>
            <span className="recon-form__upload-hint">
              {importedTx.length > 0
                ? `${importedTx.length} transação(ões) importada(s)`
                : 'Nenhum extrato importado'}
            </span>
          </div>

          {importedTx.length > 0 && (
            <div className="recon-form__preview">
              <p className="recon-form__preview-title">Pré-visualização:</p>
              <div className="recon-form__preview-table-wrap">
                <table className="recon-form__preview-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Data</th>
                      <th>Valor</th>
                      <th>Descrição</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importedTx.slice(0, 10).map((tx, idx) => (
                      <tr key={idx}>
                        <td style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{tx.transactionId.slice(0, 12)}</td>
                        <td>{formatDate(tx.date)}</td>
                        <td className={tx.value < 0 ? 'recon-negative' : ''}>{formatCurrency(tx.value)}</td>
                        <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{tx.description}</td>
                      </tr>
                    ))}
                    {importedTx.length > 10 && (
                      <tr>
                        <td colSpan={4} style={{ textAlign: 'center', color: '#74777f', padding: '0.5rem' }}>
                          ... e mais {importedTx.length - 10} transação(ões)
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* ── Confirm Modal ── */}
      <Modal
        isOpen={!!confirmId}
        onClose={() => setConfirmId(null)}
        title="Confirmar Conciliação"
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmId(null)}>Cancelar</Button>
            <Button
              variant="primary"
              style={{ background: '#002045', borderColor: '#002045' }}
              onClick={() => confirmId && handleConfirm(confirmId)}
              loading={confirmRecon.isPending}
            >
              Confirmar
            </Button>
          </>
        }
      >
        <p style={{ fontSize: '0.875rem', color: '#43474e', margin: 0 }}>
          Tem certeza que deseja confirmar esta conciliação? O saldo contábil será ajustado conforme o extrato.
        </p>
      </Modal>

      <style>{`
        .recon-page { padding: 1.5rem; }
        .state-card { text-align: center; }
        .state-error p { margin: 0; color: #ba1a1a; font-weight: 600; }
        .state-error .state-detail { color: #74777f; font-size: 0.875rem; font-weight: 400; margin-top: 0.5rem; }
        .state-empty p { margin: 0; color: #74777f; }
        .state-empty .state-detail { font-size: 0.875rem; margin-top: 0.5rem; }
        .recon-table-wrap { overflow-x: auto; }
        .recon-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .recon-table th { padding: 0.75rem 1rem; background: #f2f4f6; color: #43474e; font-weight: 600; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 2px solid #e0e3e5; text-align: left; white-space: nowrap; position: sticky; top: 0; z-index: 1;}
        .recon-table td { padding: 0.75rem 1rem; border-bottom: 1px solid #eceef0; color: #191c1e; }
        .recon-table tbody tr:hover td { background: #eceef0; }
        .recon-diff-warn { color: #c25a02; font-weight: 600; }
        .recon-negative { color: #ba1a1a; }
        .recon-actions { display: flex; gap: 0.375rem; }
        .recon-form { display: flex; flex-direction: column; gap: 1rem; min-width: 400px; }
        .recon-form__select { width: 100%; padding: 0.5rem; border: 1px solid #c4c6cf; border-radius: 4px; font-size: 0.875rem; height: 40px; }
        .recon-form__row { display: flex; gap: 0.75rem; }
        .recon-form__row > * { flex: 1; }
        .recon-form__upload { display: flex; align-items: center; gap: 0.75rem; }
        .recon-form__upload-hint { font-size: 0.8125rem; color: #74777f; }
        .recon-form__preview { border: 1px solid #e0e3e5; border-radius: 4px; padding: 0.75rem; background: #f2f4f6; }
        .recon-form__preview-title { margin: 0 0 0.5rem; font-size: 0.8125rem; font-weight: 600; color: #43474e; }
        .recon-form__preview-table-wrap { overflow-x: auto; }
        .recon-form__preview-table { width: 100%; border-collapse: collapse; font-size: 0.8125rem; }
        .recon-form__preview-table th { text-align: left; padding: 0.375rem 0.5rem; font-size: 0.75rem; color: #43474e; background: #eceef0; position: sticky; top: 0; z-index: 1;}
        .recon-form__preview-table td { padding: 0.25rem 0.5rem; border-bottom: 1px solid #eceef0; }
        .recon-detail__summary { display: flex; gap: 1rem; flex-wrap: wrap; margin-bottom: 1rem; }
        .recon-detail__stat { flex: 1; min-width: 120px; padding: 0.75rem; background: #f2f4f6; border-radius: 4px; text-align: center; }
        .recon-detail__stat-label { display: block; font-size: 12px; color: #43474e; margin-bottom: 0.25rem; font-family: 'JetBrains Mono', ui-monospace, monospace; font-weight: 500; letter-spacing: 0.02em;}
        .recon-detail__stat-value { font-size: 0.9375rem; font-weight: 600; color: #191c1e; }
        .recon-detail__table-wrap { overflow-x: auto; }
        .recon-detail__table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .recon-detail__table th { text-align: left; padding: 0.5rem 0.75rem; background: #f2f4f6; color: #43474e; font-weight: 600; font-size: 0.75rem; border-bottom: 2px solid #e0e3e5; position: sticky; top: 0; z-index: 1;}
        .recon-detail__table td { padding: 0.5rem 0.75rem; border-bottom: 1px solid #eceef0; }
        .recon-detail__notes { margin-top: 1rem; padding: 0.75rem; background: #ffddba; border-radius: 4px; font-size: 0.875rem; color: #43474e; }
      `}</style>
    </div>
  );
}
